import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { WebPushSetting } from '@/components/notifications/WebPushSetting';
import { NotificationSelfTest } from '@/components/notifications/NotificationSelfTest';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getSettings,
  updateSettings,
  getMemberData,
  isAuthenticated,
  memberApiFetch,
  type PortalSettings,
} from '@/lib/memberAuth';
import { SupportAccessList } from '@/components/impersonation/SupportAccessList';
import {
  applyTheme,
  persistThemeLocal,
  persistTimezoneLocal,
  updateMemberPreferences,
  loadMemberPreferences,
  browserTimezone,
  type MemberTheme,
} from '@/lib/memberPreferences';
import { TimezoneSelector } from '@/components/TimezoneSelector';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import {
  Settings as SettingsIcon,
  Bell,
  Mail,
  Phone,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Lock,
  User,
  Save,
  Shield,
  FileCheck,
  Calendar,
  ExternalLink,
  Info,
  Users,
  Heart,
  UserPlus,
  Trash2,
  Moon,
  Sun,
  Monitor,
  Clock,
} from 'lucide-react';

export default function MemberSettingsPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const member = getMemberData();

  const [settings, setSettings] = useState<PortalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [preferredContactMethod, setPreferredContactMethod] = useState('email');
  const [allowPortalMessages, setAllowPortalMessages] = useState(true);

  // Appearance & timezone state (mirrors staff theme + timezone preferences)
  const [themePref, setThemePref] = useState<MemberTheme>('system');
  const [timezone, setTimezone] = useState<string>(browserTimezone());

  // SMS Consent state (A2P 10DLC Compliance)
  const [smsConsentGiven, setSmsConsentGiven] = useState(false);
  const [smsConsentDate, setSmsConsentDate] = useState<string | null>(null);

  // Password change state
  const [changePasswordMode, setChangePasswordMode] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Family & Caregiver state
  interface FamilyMember {
    id: string;
    name: string;
    relationship: string;
    dateOfBirth?: string;
    isCovered: boolean;
  }
  interface Caregiver {
    id: string;
    name: string;
    email: string;
    phone?: string;
    relationship: string;
    permissions: string[];
  }
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [caregivers, setCaregivers] = useState<Caregiver[]>([]);
  const [showAddFamily, setShowAddFamily] = useState(false);
  const [showAddCaregiver, setShowAddCaregiver] = useState(false);
  const [newFamilyMember, setNewFamilyMember] = useState({ name: '', relationship: 'spouse', dateOfBirth: '', isCovered: true });
  const [newCaregiver, setNewCaregiver] = useState({ name: '', email: '', phone: '', relationship: 'family', permissions: ['view_messages', 'view_appointments'] });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }

    loadSettings();

    // Appearance & timezone come from the same GET /settings response, but we
    // read them through the deduped preferences loader (X-CBO-ID pattern).
    loadMemberPreferences().then((prefs) => {
      if (prefs) {
        setThemePref(prefs.theme);
        setTimezone(prefs.timezone);
      }
    });
  }, [navigate]);

  // Change theme: apply instantly, persist locally (instant paint) + to server.
  const handleThemeChange = async (theme: MemberTheme) => {
    setThemePref(theme);
    applyTheme(theme);
    persistThemeLocal(theme);
    try {
      await updateMemberPreferences({ theme });
      setSuccess('Appearance updated');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to save theme');
    }
  };

  // Change timezone: persist locally (for synchronous date formatting) + server.
  const handleTimezoneChange = async (tz: string) => {
    setTimezone(tz);
    persistTimezoneLocal(tz);
    try {
      await updateMemberPreferences({ timezone: tz });
      setSuccess('Timezone updated');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to save timezone');
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getSettings();
      if (data) {
        setSettings(data);

        // Update form state with fallback defaults
        setEmailNotifications(data.emailNotificationsEnabled ?? true);
        setSmsNotifications(data.smsNotificationsEnabled ?? false);
        setPreferredContactMethod(data.preferredContactMethod ?? 'email');
        setAllowPortalMessages(data.allowPortalMessages ?? true);
      } else {
        // Use defaults if no settings returned
        console.warn('No settings data returned, using defaults');
      }
    } catch (err: any) {
      console.error('Error loading settings:', err);
      setError(err.message || 'Failed to load settings. Using default values.');
      // Continue with default values - don't crash
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updatedSettings: PortalSettings = {
        emailNotificationsEnabled: emailNotifications,
        smsNotificationsEnabled: smsNotifications,
        preferredContactMethod,
        allowPortalMessages,
      };

      await updateSettings(updatedSettings);
      setSuccess('Settings saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    // TODO: Implement password change API endpoint
    setError('Password change is not yet implemented in the API');
    // This would need a new API endpoint in the backend
  };

  const pageHeader = (
    <PageHeader
      leading={
        <Link to="/member/dashboard">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('settings.back', 'Back')}
          </Button>
        </Link>
      }
      title={t('settings.settings', 'Settings')}
      description={t('settings.manage_your_account_preferences', 'Manage your account preferences')}
    />
  );

  if (loading) {
    return (
      <>
        {pageHeader}
        <PageContainer size="narrow">
          <PageState variant="loading" />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {pageHeader}
      <PageContainer size="narrow">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">{success}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          {/* Account Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <User className="w-5 h-5 text-teal-600" />
                <CardTitle>{t('settings.account_information', 'Account Information')}</CardTitle>
              </div>
              <CardDescription>{t('settings.your_basic_account_details', 'Your basic account details')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-ink-700">{t('settings.first_name', 'First Name')}</Label>
                  <p className="mt-1 text-ink">{member?.first_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-ink-700">{t('settings.last_name', 'Last Name')}</Label>
                  <p className="mt-1 text-ink">{member?.last_name}</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-ink-700">{t('settings.email', 'Email')}</Label>
                <p className="mt-1 text-ink">{member?.email}</p>
              </div>
              <p className="text-sm text-ink-500">
                {t('settings.to_update_your_account_information', 'To update your account information, please contact your case manager.')}
              </p>
            </CardContent>
          </Card>

          {/* Support access (TRUS-747): when WeaveIn support viewed the portal as you */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-teal-600" />
                <CardTitle>{t('settings.support_access', 'Support access')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <SupportAccessList fetcher={memberApiFetch} />
            </CardContent>
          </Card>

          {/* Appearance & Timezone */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Moon className="w-5 h-5 text-teal-600" />
                <CardTitle>{t('settings.appearance', 'Appearance & Time')}</CardTitle>
              </div>
              <CardDescription>
                {t('settings.appearance_desc', 'Choose your theme and the timezone used to display dates and times')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme */}
              <div className="space-y-2">
                <Label className="font-medium">{t('settings.theme', 'Theme')}</Label>
                <div className="grid grid-cols-3 gap-2 max-w-md">
                  {([
                    { value: 'light' as MemberTheme, label: t('ui.light', 'Light'), icon: Sun },
                    { value: 'dark' as MemberTheme, label: t('ui.dark', 'Dark'), icon: Moon },
                    { value: 'system' as MemberTheme, label: t('ui.system', 'System'), icon: Monitor },
                  ]).map((opt) => {
                    const Icon = opt.icon;
                    const active = themePref === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleThemeChange(opt.value)}
                        aria-pressed={active}
                        className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border px-3 py-3 text-sm font-medium transition-colors ${
                          active
                            ? 'border-v2accent bg-v2accent/10 text-v2accent'
                            : 'border-v2border text-ink-700 hover:bg-paper-2'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-sm text-ink-500">
                  {t('settings.theme_help', 'System follows your device’s light/dark setting.')}
                </p>
              </div>

              {/* Timezone */}
              <div className="space-y-2">
                <Label className="font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4 text-ink-500" />
                  {t('settings.timezone', 'Timezone')}
                </Label>
                <div className="max-w-md">
                  <TimezoneSelector
                    value={timezone}
                    onChange={handleTimezoneChange}
                    showLabel={false}
                    showAllTimezones
                  />
                </div>
                <p className="text-sm text-ink-500">
                  {t('settings.timezone_help', 'Dates and times across the portal are shown in this timezone.')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Family & Caregivers Section */}
          <Card className="border-pink-200 dark:border-pink-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                <CardTitle>{t('settings.family_caregivers', 'Family & Caregivers')}</CardTitle>
              </div>
              <CardDescription>
                {t('settings.manage_family_and_caregivers', 'Manage family members and authorize caregivers to help with your care')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dependents / Family Members */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-ink flex items-center">
                    <Heart className="w-4 h-4 mr-2 text-pink-500" />
                    {t('settings.family_members', 'Family Members & Dependents')}
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddFamily(!showAddFamily)}
                    className="text-pink-600 border-pink-300 hover:bg-pink-50"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    {t('settings.add_family_member', 'Add Family Member')}
                  </Button>
                </div>

                {showAddFamily && (
                  <div className="bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-700 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="familyName">{t('settings.full_name', 'Full Name')}</Label>
                        <Input
                          id="familyName"
                          value={newFamilyMember.name}
                          onChange={(e) => setNewFamilyMember({ ...newFamilyMember, name: e.target.value })}
                          placeholder="Enter name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="familyRelationship">{t('settings.relationship', 'Relationship')}</Label>
                        <Select
                          value={newFamilyMember.relationship}
                          onValueChange={(value) => setNewFamilyMember({ ...newFamilyMember, relationship: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="spouse">{t('settings.spouse_partner', 'Spouse/Partner')}</SelectItem>
                            <SelectItem value="child">{t('settings.child', 'Child')}</SelectItem>
                            <SelectItem value="parent">{t('settings.parent', 'Parent')}</SelectItem>
                            <SelectItem value="sibling">{t('settings.sibling', 'Sibling')}</SelectItem>
                            <SelectItem value="other">{t('settings.other_family', 'Other Family')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="familyDob">{t('settings.date_of_birth', 'Date of Birth')}</Label>
                        <Input
                          id="familyDob"
                          type="date"
                          value={newFamilyMember.dateOfBirth}
                          onChange={(e) => setNewFamilyMember({ ...newFamilyMember, dateOfBirth: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex items-center space-x-2 mt-6">
                        <Checkbox
                          id="familyCovered"
                          checked={newFamilyMember.isCovered}
                          onCheckedChange={(checked) => setNewFamilyMember({ ...newFamilyMember, isCovered: checked as boolean })}
                        />
                        <Label htmlFor="familyCovered" className="cursor-pointer text-sm">
                          {t('settings.covered_under_my_benefits', 'Covered under my benefits')}
                        </Label>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          if (newFamilyMember.name) {
                            setFamilyMembers([...familyMembers, { ...newFamilyMember, id: Date.now().toString() }]);
                            setNewFamilyMember({ name: '', relationship: 'spouse', dateOfBirth: '', isCovered: true });
                            setShowAddFamily(false);
                          }
                        }}
                        className="bg-pink-600 hover:bg-pink-700"
                      >
                        {t('settings.add', 'Add')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setShowAddFamily(false)}>
                        {t('settings.cancel', 'Cancel')}
                      </Button>
                    </div>
                  </div>
                )}

                {familyMembers.length > 0 ? (
                  <div className="space-y-2">
                    {familyMembers.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-3 bg-paper rounded-lg">
                        <div>
                          <p className="font-medium text-ink">{member.name}</p>
                          <p className="text-sm text-ink-500">
                            {member.relationship.charAt(0).toUpperCase() + member.relationship.slice(1)}
                            {member.isCovered && ' • Covered'}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFamilyMembers(familyMembers.filter(m => m.id !== member.id))}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-ink-500 italic">
                    {t('settings.no_family_members', 'No family members added yet. Add dependents who are covered under your benefits.')}
                  </p>
                )}
              </div>

              {/* Authorized Caregivers */}
              <div className="border-t pt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-ink flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-purple-500" />
                      {t('settings.authorized_caregivers', 'Authorized Caregivers')}
                    </h4>
                    <p className="text-sm text-ink-500 mt-1">
                      {t('settings.caregiver_description', 'Grant trusted individuals access to help manage your care')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddCaregiver(!showAddCaregiver)}
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    {t('settings.add_caregiver', 'Add Caregiver')}
                  </Button>
                </div>

                {showAddCaregiver && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="caregiverName">{t('settings.caregiver_name', 'Caregiver Name')}</Label>
                        <Input
                          id="caregiverName"
                          value={newCaregiver.name}
                          onChange={(e) => setNewCaregiver({ ...newCaregiver, name: e.target.value })}
                          placeholder="Enter name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="caregiverEmail">{t('settings.caregiver_email', 'Email Address')}</Label>
                        <Input
                          id="caregiverEmail"
                          type="email"
                          value={newCaregiver.email}
                          onChange={(e) => setNewCaregiver({ ...newCaregiver, email: e.target.value })}
                          placeholder="email@example.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="caregiverPhone">{t('settings.caregiver_phone', 'Phone Number')}</Label>
                        <Input
                          id="caregiverPhone"
                          value={newCaregiver.phone}
                          onChange={(e) => setNewCaregiver({ ...newCaregiver, phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="caregiverRelation">{t('settings.caregiver_relationship', 'Relationship')}</Label>
                        <Select
                          value={newCaregiver.relationship}
                          onValueChange={(value) => setNewCaregiver({ ...newCaregiver, relationship: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="family">{t('settings.family_member', 'Family Member')}</SelectItem>
                            <SelectItem value="friend">{t('settings.friend', 'Friend')}</SelectItem>
                            <SelectItem value="professional">{t('settings.professional_caregiver', 'Professional Caregiver')}</SelectItem>
                            <SelectItem value="healthcare">{t('settings.healthcare_provider', 'Healthcare Provider')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('settings.caregiver_permissions', 'Permissions')}</Label>
                      <div className="flex flex-wrap gap-3">
                        {[
                          { id: 'view_messages', label: 'View Messages' },
                          { id: 'send_messages', label: 'Send Messages' },
                          { id: 'view_appointments', label: 'View Appointments' },
                          { id: 'schedule_appointments', label: 'Schedule Appointments' },
                          { id: 'view_documents', label: 'View Documents' },
                          { id: 'upload_documents', label: 'Upload Documents' },
                        ].map((perm) => (
                          <div key={perm.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={perm.id}
                              checked={newCaregiver.permissions.includes(perm.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setNewCaregiver({ ...newCaregiver, permissions: [...newCaregiver.permissions, perm.id] });
                                } else {
                                  setNewCaregiver({ ...newCaregiver, permissions: newCaregiver.permissions.filter(p => p !== perm.id) });
                                }
                              }}
                            />
                            <Label htmlFor={perm.id} className="text-sm cursor-pointer">{perm.label}</Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          if (newCaregiver.name && newCaregiver.email) {
                            setCaregivers([...caregivers, { ...newCaregiver, id: Date.now().toString() }]);
                            setNewCaregiver({ name: '', email: '', phone: '', relationship: 'family', permissions: ['view_messages', 'view_appointments'] });
                            setShowAddCaregiver(false);
                          }
                        }}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {t('settings.add_caregiver', 'Add Caregiver')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setShowAddCaregiver(false)}>
                        {t('settings.cancel', 'Cancel')}
                      </Button>
                    </div>
                  </div>
                )}

                {caregivers.length > 0 ? (
                  <div className="space-y-2">
                    {caregivers.map((cg) => (
                      <div key={cg.id} className="flex items-center justify-between p-3 bg-paper rounded-lg">
                        <div>
                          <p className="font-medium text-ink">{cg.name}</p>
                          <p className="text-sm text-ink-500">{cg.email}</p>
                          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                            {cg.permissions.length} permission{cg.permissions.length !== 1 ? 's' : ''} granted
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCaregivers(caregivers.filter(c => c.id !== cg.id))}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-ink-500 italic">
                    {t('settings.no_caregivers', 'No caregivers added. Add trusted individuals who can help manage your care.')}
                  </p>
                )}

                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mt-4">
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    <strong>{t('settings.important', 'Important:')}</strong> {t('settings.caregiver_notice', 'Caregivers will receive an email invitation to create their account. They can only access information based on the permissions you grant. You can revoke access at any time.')}
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-pink-600 hover:bg-pink-700"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('settings.saving', 'Saving...')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('settings.save_family_settings', 'Save Family & Caregiver Settings')}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Health Plan Information - Generic fields for all insurance types */}
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <CardTitle>{t('settings.health_coverage_information', 'Health Coverage Information')}</CardTitle>
              </div>
              <CardDescription>
                {t('settings.your_insurance_and_coverage_details', 'Your insurance and coverage details')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Coverage Type Selection */}
              <div className="space-y-3">
                <Label className="font-medium">{t('settings.coverage_type', 'Coverage Type')}</Label>
                <Select defaultValue="commercial">
                  <SelectTrigger>
                    <SelectValue placeholder="Select coverage type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medicaid">{t('settings.medicaid', 'Medicaid')}</SelectItem>
                    <SelectItem value="medicare">{t('settings.medicare', 'Medicare')}</SelectItem>
                    <SelectItem value="dual_eligible">{t('settings.medicare_medicaid_dual', 'Medicare/Medicaid (Dual Eligible)')}</SelectItem>
                    <SelectItem value="commercial">{t('settings.commercial_employer', 'Commercial (Employer/Individual)')}</SelectItem>
                    <SelectItem value="marketplace">{t('settings.marketplace_aca', 'Marketplace (ACA)')}</SelectItem>
                    <SelectItem value="military">{t('settings.tricare_va', 'TRICARE/VA')}</SelectItem>
                    <SelectItem value="uninsured">{t('settings.uninsured', 'Uninsured')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-ink-500">
                  {t('settings.coverage_type_help', 'This helps us show you the right benefits and resources')}
                </p>
              </div>

              {/* Primary Health Plan */}
              <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100">
                  {t('settings.primary_health_plan', 'Primary Health Plan')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="planName">{t('settings.plan_name_carrier', 'Plan Name / Carrier')}</Label>
                    <Input id="planName" placeholder="e.g., Blue Cross, Anthem, Molina" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="memberId">{t('settings.member_id', 'Member ID / Subscriber ID')}</Label>
                    <Input id="memberId" placeholder="Enter your member ID" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="groupNumber">{t('settings.group_number', 'Group Number (if applicable)')}</Label>
                    <Input id="groupNumber" placeholder="Group or policy number" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="planType">{t('settings.plan_type', 'Plan Type')}</Label>
                    <Select>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select plan type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hmo">{t('settings.hmo', 'HMO')}</SelectItem>
                        <SelectItem value="ppo">{t('settings.ppo', 'PPO')}</SelectItem>
                        <SelectItem value="epo">{t('settings.epo', 'EPO')}</SelectItem>
                        <SelectItem value="pos">{t('settings.pos', 'POS')}</SelectItem>
                        <SelectItem value="hdhp">{t('settings.hdhp', 'HDHP')}</SelectItem>
                        <SelectItem value="mco">{t('settings.mco', 'Managed Care (MCO)')}</SelectItem>
                        <SelectItem value="ffs">{t('settings.ffs', 'Fee-for-Service')}</SelectItem>
                        <SelectItem value="medicare_advantage">{t('settings.medicare_advantage', 'Medicare Advantage')}</SelectItem>
                        <SelectItem value="medicare_supplement">{t('settings.medigap', 'Medigap/Medicare Supplement')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="effectiveDate">{t('settings.effective_date', 'Effective Date')}</Label>
                    <Input id="effectiveDate" type="date" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="terminationDate">{t('settings.termination_date', 'Termination Date (if known)')}</Label>
                    <Input id="terminationDate" type="date" className="mt-1" />
                  </div>
                </div>
              </div>

              {/* Medicare-Specific Section (conditional display) */}
              <div className="space-y-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                <h4 className="font-semibold text-purple-900 dark:text-purple-100 flex items-center">
                  {t('settings.medicare_details', 'Medicare Details')}
                  <span className="ml-2 text-xs font-normal text-purple-600 dark:text-purple-400">(if applicable)</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="medicareId">{t('settings.medicare_beneficiary_id', 'Medicare Beneficiary ID (MBI)')}</Label>
                    <Input id="medicareId" placeholder="11-character MBI" className="mt-1" />
                  </div>
                  <div>
                    <Label>{t('settings.medicare_parts', 'Medicare Parts')}</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox id="partA" />
                        <Label htmlFor="partA" className="text-sm cursor-pointer">{t('settings.part_a_hospital', 'Part A (Hospital)')}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="partB" />
                        <Label htmlFor="partB" className="text-sm cursor-pointer">{t('settings.part_b_medical', 'Part B (Medical)')}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="partC" />
                        <Label htmlFor="partC" className="text-sm cursor-pointer">{t('settings.part_c_advantage', 'Part C (Advantage)')}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="partD" />
                        <Label htmlFor="partD" className="text-sm cursor-pointer">{t('settings.part_d_prescription', 'Part D (Prescription)')}</Label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Medicaid-Specific Section */}
              <div className="space-y-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                <h4 className="font-semibold text-green-900 dark:text-green-100 flex items-center">
                  {t('settings.medicaid_details', 'Medicaid Details')}
                  <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">(if applicable)</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="medicaidId">{t('settings.medicaid_id', 'Medicaid ID / State ID')}</Label>
                    <Input id="medicaidId" placeholder="Your Medicaid ID" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="mcpName">{t('settings.mcp_health_plan', 'Managed Care Plan (MCP) / Health Plan')}</Label>
                    <Input id="mcpName" placeholder="e.g., L.A. Care, Molina, Kaiser" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="pcpName">{t('settings.primary_care_provider', 'Primary Care Provider (PCP)')}</Label>
                    <Input id="pcpName" placeholder="Your assigned PCP name" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="countyOfResidence">{t('settings.county_of_residence', 'County of Residence')}</Label>
                    <Input id="countyOfResidence" placeholder="County" className="mt-1" />
                  </div>
                </div>
              </div>

              {/* Secondary/Supplemental Coverage */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Checkbox id="hasSecondary" />
                  <Label htmlFor="hasSecondary" className="font-medium cursor-pointer">
                    {t('settings.i_have_secondary_coverage', 'I have secondary or supplemental coverage')}
                  </Label>
                </div>
                <div className="pl-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="secondaryPlan">{t('settings.secondary_plan_name', 'Secondary Plan Name')}</Label>
                      <Input id="secondaryPlan" placeholder="Secondary carrier/plan" className="mt-1" disabled />
                    </div>
                    <div>
                      <Label htmlFor="secondaryMemberId">{t('settings.secondary_member_id', 'Secondary Member ID')}</Label>
                      <Input id="secondaryMemberId" placeholder="Member ID" className="mt-1" disabled />
                    </div>
                  </div>
                </div>
              </div>

              {/* QLE Notice */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-2 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {t('settings.qualifying_life_event', 'Qualifying Life Event (QLE)')}
                </h4>
                <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                  {t('settings.qle_description', 'If you\'ve experienced a life change (marriage, birth, job change, loss of coverage), you may be eligible for a special enrollment period.')}
                </p>
                <Button variant="outline" size="sm" className="border-amber-400 text-amber-700 hover:bg-amber-100">
                  {t('settings.report_life_event', 'Report a Life Event')}
                </Button>
              </div>

              <div className="pt-2">
                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-v2accent hover:bg-v2accent/90"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('settings.saving', 'Saving...')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('settings.save_health_information', 'Save Health Information')}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Bell className="w-5 h-5 text-teal-600" />
                <CardTitle>{t('settings.notification_preferences', 'Notification Preferences')}</CardTitle>
              </div>
              <CardDescription>{t('settings.choose_how_you_want_to', 'Choose how you want to receive notifications')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Browser push (TRUS-668) */}
              <WebPushSetting portal="member" />
              {/* Send a test notification: push and the in-app check (TRUS-668) */}
              <NotificationSelfTest portal="member" />

              {/* Email Notifications */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="emailNotifications"
                  checked={emailNotifications}
                  onCheckedChange={(checked) => setEmailNotifications(checked as boolean)}
                  disabled={saving}
                />
                <div className="flex-1">
                  <Label htmlFor="emailNotifications" className="cursor-pointer font-medium">
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-ink-500" />
                      <span>{t('settings.email_notifications', 'Email Notifications')}</span>
                    </div>
                  </Label>
                  <p className="text-sm text-ink-500 mt-1">
                    {t('settings.receive_email_notifications_for_new', 'Receive email notifications for new messages and updates')}
                  </p>
                </div>
              </div>

              {/* SMS Notifications */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="smsNotifications"
                  checked={smsNotifications}
                  onCheckedChange={(checked) => setSmsNotifications(checked as boolean)}
                  disabled={saving}
                />
                <div className="flex-1">
                  <Label htmlFor="smsNotifications" className="cursor-pointer font-medium">
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-ink-500" />
                      <span>{t('settings.sms_notifications', 'SMS Notifications')}</span>
                    </div>
                  </Label>
                  <p className="text-sm text-ink-500 mt-1">
                    {t('settings.receive_text_message_notifications_for', 'Receive text message notifications for urgent messages')}
                  </p>
                </div>
              </div>

              {/* Portal Messages */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="allowPortalMessages"
                  checked={allowPortalMessages}
                  onCheckedChange={(checked) => setAllowPortalMessages(checked as boolean)}
                  disabled={saving}
                />
                <div className="flex-1">
                  <Label htmlFor="allowPortalMessages" className="cursor-pointer font-medium">
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="w-4 h-4 text-ink-500" />
                      <span>{t('settings.portal_messages', 'Portal Messages')}</span>
                    </div>
                  </Label>
                  <p className="text-sm text-ink-500 mt-1">
                    {t('settings.allow_case_managers_to_send', 'Allow case managers to send you messages through the portal')}
                  </p>
                </div>
              </div>

              {/* Preferred Contact Method */}
              <div>
                <Label htmlFor="preferredContactMethod" className="font-medium">
                  {t('settings.preferred_contact_method', 'Preferred Contact Method')}
                </Label>
                <Select
                  value={preferredContactMethod}
                  onValueChange={setPreferredContactMethod}
                  disabled={saving}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">{t('settings.email_1', 'Email')}</SelectItem>
                    <SelectItem value="sms">{t('settings.sms_text_message', 'SMS / Text Message')}</SelectItem>
                    <SelectItem value="phone">{t('settings.phone_call', 'Phone Call')}</SelectItem>
                    <SelectItem value="portal">{t('settings.portal_messages_only', 'Portal Messages Only')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-ink-500 mt-2">
                  {t('settings.your_case_manager_will_use', 'Your case manager will use this as the primary way to reach you')}
                </p>
              </div>

              <div className="pt-4">
                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-v2accent hover:bg-v2accent/90"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('settings.saving', 'Saving...')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('settings.save_preferences', 'Save Preferences')}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* SMS Consent & Compliance (A2P 10DLC) */}
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <CardTitle>{t('settings.sms_messaging_consent', 'SMS Messaging Consent')}</CardTitle>
              </div>
              <CardDescription>{t('settings.manage_your_text_message_communication', 'Manage your text message communication preferences')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100">{t('settings.about_sms_communications', 'About SMS Communications')}</h4>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  By opting in to SMS notifications, you consent to receive text messages from Trust.Care
                  and your care team regarding appointments, reminders, and important updates about your care.
                </p>
                <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <p><strong>{t('settings.message_frequency', 'Message Frequency:')}</strong> {t('settings.varies_based_on_your_care', 'Varies based on your care needs. You may receive up to 10 messages per month.')}</p>
                  <p><strong>{t('settings.carrier_charges', 'Carrier Charges:')}</strong> {t('settings.message_and_data_rates_may', 'Message and data rates may apply from your mobile carrier.')}</p>
                  <p><strong>{t('settings.your_rights', 'Your Rights:')}</strong> {t('settings.you_can_opt_out_at', 'You can opt out at any time using the commands below.')}</p>
                </div>
              </div>

              {/* SMS Control Commands */}
              <div className="bg-paper border border-v2border rounded-lg p-4">
                <h4 className="font-semibold text-ink mb-3">{t('settings.sms_control_commands', 'SMS Control Commands')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start space-x-2">
                    <span className="font-mono bg-paper-2 dark:bg-gray-700 px-2 py-1 rounded text-xs">STOP</span>
                    <p className="text-ink-700 flex-1">
                      {t('settings.text', 'Text')} <strong>STOP</strong> to immediately unsubscribe from all SMS messages.
                      You will receive one final confirmation message.
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="font-mono bg-paper-2 dark:bg-gray-700 px-2 py-1 rounded text-xs">START</span>
                    <p className="text-ink-700 flex-1">
                      {t('settings.text_1', 'Text')} <strong>START</strong> to resubscribe and start receiving messages again.
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="font-mono bg-paper-2 dark:bg-gray-700 px-2 py-1 rounded text-xs">HELP</span>
                    <p className="text-ink-700 flex-1">
                      {t('settings.text_2', 'Text')} <strong>HELP</strong> for support information and to view these instructions again.
                    </p>
                  </div>
                </div>
              </div>

              {/* Consent Checkbox */}
              <div className="border-t pt-4">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="smsConsent"
                    checked={smsConsentGiven}
                    onCheckedChange={(checked) => {
                      setSmsConsentGiven(checked as boolean);
                      if (checked) {
                        setSmsConsentDate(new Date().toISOString());
                      }
                    }}
                  />
                  <div className="flex-1">
                    <Label htmlFor="smsConsent" className="cursor-pointer font-medium text-ink">
                      {t('settings.i_consent_to_receive_sms', 'I consent to receive SMS text messages')}
                    </Label>
                    <p className="text-sm text-ink-500 mt-1">
                      I understand and agree to receive text messages at the phone number on file.
                      I can opt out at any time by texting STOP. Standard message and data rates may apply.
                    </p>
                    {smsConsentGiven && smsConsentDate && (
                      <p className="text-xs text-green-700 dark:text-green-400 mt-2 flex items-center">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Consent given on {new Date(smsConsentDate).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Privacy & Compliance Notice */}
              <div className="text-xs text-ink-500 bg-paper p-3 rounded">
                <p className="font-semibold mb-1">{t('settings.privacy_compliance', 'Privacy & Compliance:')}</p>
                <p>
                  This service complies with A2P 10DLC (Application-to-Person 10-Digit Long Code) regulations
                  and TCPA (Telephone Consumer Protection Act) requirements. Your consent is recorded and can be
                  revoked at any time. We will never sell your phone number to third parties. For more information,
                  see our <Link to="/consent/privacy-policy" className="text-blue-600 hover:underline">{t('settings.privacy_policy', 'Privacy Policy')}</Link>
                  {' '}and <Link to="/consent/terms-of-use" className="text-blue-600 hover:underline">{t('settings.terms_of_use', 'Terms of Use')}</Link>.
                </p>
              </div>

              {/* Save Button */}
              <div className="pt-2">
                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-v2accent hover:bg-v2accent/90"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('settings.saving_1', 'Saving...')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('settings.save_sms_preferences', 'Save SMS Preferences')}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Compliance & Required Actions */}
          <Card className="border-orange-200 dark:border-orange-800">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <CardTitle>{t('settings.compliance_required_actions', 'Compliance & Required Actions')}</CardTitle>
              </div>
              <CardDescription>
                {t('settings.complete_these_actions_to_maintain', 'Complete these actions to maintain your enrollment and access to services')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Action Items List */}
              <div className="space-y-3">
                {/* Action 1: Verify Contact Information */}
                <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <FileCheck className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-ink mb-1">
                          {t('settings.verify_contact_information', 'Verify Contact Information')}
                        </h4>
                        <p className="text-sm text-ink-700 mb-2">
                          Confirm that your phone number, email, and address are current to
                          receive important updates about your care.
                        </p>
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                          >
                            {t('settings.review_contact_info', 'Review Contact Info')}
                          </Button>
                          <span className="text-xs text-ink-500">{t('settings.last_verified_never', 'Last verified: Never')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 bg-orange-100 dark:bg-orange-900/40 px-2 py-1 rounded text-xs font-medium text-orange-700 dark:text-orange-300">
                      <AlertCircle className="w-3 h-3" />
                      <span>{t('settings.action_needed', 'Action Needed')}</span>
                    </div>
                  </div>
                </div>

                {/* Action 2: Update Insurance Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <FileCheck className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-ink mb-1">
                          Insurance Information (if applicable)
                        </h4>
                        <p className="text-sm text-ink-700 mb-2">
                          If you have Medicaid, Medicare, or private insurance, please ensure
                          your coverage details are up to date.
                        </p>
                        <div className="flex items-center space-x-2">
                          <Link to="/member/dashboard">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                            >
                              {t('settings.update_insurance', 'Update Insurance')}
                            </Button>
                          </Link>
                          <span className="text-xs text-ink-500">{t('settings.optional', 'Optional')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 bg-blue-100 px-2 py-1 rounded text-xs font-medium text-blue-700">
                      <Info className="w-3 h-3" />
                      <span>{t('settings.optional_1', 'Optional')}</span>
                    </div>
                  </div>
                </div>

                {/* Action 3: Annual Recertification */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <Calendar className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-ink mb-1">
                          {t('settings.annual_enrollment_recertification', 'Annual Enrollment Recertification')}
                        </h4>
                        <p className="text-sm text-ink-700 mb-2">
                          Confirm your continued participation in care coordination programs.
                          You'll be notified when your recertification is due.
                        </p>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-ink-500 font-medium">
                            {t('settings.next_due_contact_your_case', 'Next due: Contact your case manager')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 bg-green-100 px-2 py-1 rounded text-xs font-medium text-green-700">
                      <CheckCircle className="w-3 h-3" />
                      <span>{t('settings.up_to_date', 'Up to Date')}</span>
                    </div>
                  </div>
                </div>

                {/* Action 4: Review Consent Documents */}
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <FileCheck className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-ink mb-1">
                          {t('settings.review_consent_documents', 'Review Consent Documents')}
                        </h4>
                        <p className="text-sm text-ink-700 mb-2">
                          {t('settings.review_your_signed_terms_of', 'Review your signed Terms of Use and HIPAA Authorization at any time.')}
                        </p>
                        <div className="flex items-center space-x-2 flex-wrap gap-2">
                          <Link to="/consent/terms-of-use" target="_blank">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-teal-300 text-teal-700 hover:bg-teal-100"
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              {t('settings.terms_of_use_1', 'Terms of Use')}
                            </Button>
                          </Link>
                          <Link to="/consent/hipaa-authorization" target="_blank">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-teal-300 text-teal-700 hover:bg-teal-100"
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              {t('settings.hipaa_authorization', 'HIPAA Authorization')}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 bg-green-100 px-2 py-1 rounded text-xs font-medium text-green-700">
                      <CheckCircle className="w-3 h-3" />
                      <span>{t('settings.signed', 'Signed')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Help Text */}
              <div className="bg-paper border border-v2border rounded-lg p-3 mt-4">
                <p className="text-xs text-ink-700">
                  <strong>{t('settings.need_help', 'Need help?')}</strong> Contact your case manager if you have questions
                  about any required actions or if you need assistance completing them.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Security */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Lock className="w-5 h-5 text-teal-600" />
                <CardTitle>{t('settings.security', 'Security')}</CardTitle>
              </div>
              <CardDescription>{t('settings.manage_your_password_and_security', 'Manage your password and security settings')}</CardDescription>
            </CardHeader>
            <CardContent>
              {!changePasswordMode ? (
                <div>
                  <p className="text-sm text-ink-500 mb-4">
                    {t('settings.keep_your_account_secure_by', 'Keep your account secure by using a strong password')}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setChangePasswordMode(true)}
                  >
                    {t('settings.change_password', 'Change Password')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="currentPassword">{t('settings.current_password', 'Current Password')}</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPassword">{t('settings.new_password', 'New Password')}</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="mt-2"
                    />
                    <p className="text-xs text-ink-500 mt-1">
                      {t('settings.must_be_at_least_8', 'Must be at least 8 characters')}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">{t('settings.confirm_new_password', 'Confirm New Password')}</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-2">
                    <Button
                      onClick={handleChangePassword}
                      className="bg-v2accent hover:bg-v2accent/90"
                    >
                      {t('settings.update_password', 'Update Password')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setChangePasswordMode(false);
                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                    >
                      {t('settings.cancel', 'Cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Privacy Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.privacy_data', 'Privacy & Data')}</CardTitle>
              <CardDescription>{t('settings.how_your_information_is_used', 'How your information is used and protected')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-ink-700">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <p>{t('settings.your_data_is_encrypted_and', 'Your data is encrypted and securely stored')}</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <p>{t('settings.only_authorized_case_managers_can', 'Only authorized case managers can access your information')}</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <p>{t('settings.you_can_mark_notes_as', 'You can mark notes as private to keep them for your eyes only')}</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <p>{t('settings.we_comply_with_hipaa_and', 'We comply with HIPAA and other privacy regulations')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </>
  );
}
